import {
  doc,
  setDoc,
  updateDoc,
  type DocumentReference,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { groupPushDocumentConverter } from "@kekkle/shared/convertors/web/group-push-document.converter";
import type {
  GroupPushDocument,
  GroupPushDocumentSettings,
  GroupPushDocumentTokens,
} from "@kekkle/shared/types/documents/group-push-document/group-push-document";
import type { SupportedLanguage } from "@kekkle/shared/types/fields/supported-language/supported-language";

export function normalizeSupportedLanguage(
  language: string | undefined,
): SupportedLanguage {
  return language === "nl" ? "nl" : "en";
}

export function groupPushDocumentReference(
  groupId: string,
): DocumentReference<GroupPushDocument> {
  return doc(firestore, `groups/${groupId}/settings/push`).withConverter(
    groupPushDocumentConverter,
  );
}

function isNotFoundError(error: unknown): boolean {
  return (error as FirestoreError).code === "not-found";
}

async function createMinimalPushDocument(params: {
  groupId: string;
  userId: string;
  language: SupportedLanguage;
}): Promise<void> {
  await setDoc(groupPushDocumentReference(params.groupId), {
    settings: {},
    tokens: {},
    language: {
      [params.userId]: params.language,
    },
  });
}

/**
 * Syncs the current user's language into each group push settings document.
 * The settings/tokens defaults make first-time creates valid under Firestore rules.
 */
export async function syncUserPushLanguageForGroups(params: {
  userId: string;
  groupIds: string[];
  language: string | undefined;
}): Promise<void> {
  const language = normalizeSupportedLanguage(params.language);

  for (const groupId of params.groupIds) {
    const pushDoc = groupPushDocumentReference(groupId);
    try {
      await updateDoc(pushDoc, {
        [`language.${params.userId}`]: language,
      });
    } catch (e) {
      if (isNotFoundError(e)) {
        try {
          await createMinimalPushDocument({
            groupId,
            userId: params.userId,
            language,
          });
        } catch (createError) {
          handleFirestoreException({ e: createError as FirestoreError });
        }
        continue;
      }
      handleFirestoreException({ e: e as FirestoreError });
    }
  }
}

export async function upsertUserPushSettingsForGroup(params: {
  groupId: string;
  userId: string;
  settings: GroupPushDocumentSettings[string];
  language: string | undefined;
}): Promise<void> {
  const language = normalizeSupportedLanguage(params.language);
  const pushDoc = groupPushDocumentReference(params.groupId);

  try {
    await updateDoc(pushDoc, {
      [`settings.${params.userId}`]: params.settings,
      [`language.${params.userId}`]: language,
    });
  } catch (e) {
    if (isNotFoundError(e)) {
      await setDoc(pushDoc, {
        settings: {
          [params.userId]: params.settings,
        },
        tokens: {},
        language: {
          [params.userId]: language,
        },
      });
      return;
    }
    throw e;
  }
}

export async function upsertUserPushTokensForGroup(params: {
  groupId: string;
  userId: string;
  tokens: GroupPushDocumentTokens[string];
  language: string | undefined;
}): Promise<void> {
  const language = normalizeSupportedLanguage(params.language);
  const pushDoc = groupPushDocumentReference(params.groupId);

  try {
    await updateDoc(pushDoc, {
      [`tokens.${params.userId}`]: params.tokens,
      [`language.${params.userId}`]: language,
    });
  } catch (e) {
    if (isNotFoundError(e)) {
      await setDoc(pushDoc, {
        settings: {},
        tokens: {
          [params.userId]: params.tokens,
        },
        language: {
          [params.userId]: language,
        },
      });
      return;
    }
    throw e;
  }
}
